import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Building2, TrendingUp, Users, Target, Zap, Globe, Heart, ShoppingBag, RefreshCw } from 'lucide-react';

const PropertyPartnersPage = () => {
  useEffect(() => {
    document.title = 'For Property Partners | Nexus LBE';
  }, []);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-10 lg:pt-20 lg:pb-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <Badge variant="outline">For Property Partners</Badge>
              <h1 className="font-['Geist'] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
                Activate dormant spaces with predictable, high-footfall attractions
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Formatted LBE experiences that drive dwell time, F&B uplift, and consistent revenue
              </p>
            </div>
            <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-secondary shadow-2xl">
              <img 
                src="/images/property-partner.jpg" 
                alt="Property partners - activating underused spaces with standardized venue format"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              Why property owners partner with us
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-3">
                <Building2 className="h-10 w-10 text-primary" />
                <h3 className="font-['Geist'] text-xl">Activate Dormant Spaces</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Our standardized ~1,600 sqm format fits multiple property types, turning vacant retail into 
                  vibrant entertainment hubs that drive footfall.
                </p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-3">
                <RefreshCw className="h-10 w-10 text-primary" />
                <h3 className="font-['Geist'] text-xl">Rotating Content, Constant Infrastructure</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Like theatres swapping shows, our format allows fresh IP experiences while the core venue 
                  infrastructure remains constant—keeping domestic audiences returning.
                </p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-3">
                <TrendingUp className="h-10 w-10 text-primary" />
                <h3 className="font-['Geist'] text-xl">Predictable Revenue Model</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Diversified streams (tickets, merch, F&B) with proven unit economics. Scalable rental 
                  opportunity with halo effects for surrounding retail.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why We're Here - Convergence of Need */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              A Convergence of Need
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Multiple market forces align to create an unprecedented opportunity for property partners
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-lg font-semibold">Immersive Tech Now Mainstream</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  AR/VR and real-time rendering are cost-efficient and culturally expected, creating demand for 
                  scalable LBEs in city centers when combined with live-action elements.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Globe className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-lg font-semibold">Urban Density in Europe & Middle East</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  High population density creates larger catchment areas for attractions, making LBEs economically 
                  viable with predictable repeat visitation and footfall.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Heart className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-lg font-semibold">Consumer Shift Towards Experiences</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Millennials and Gen Z prioritize memories over material goods. LBEs provide immersive, social, 
                  "Instagrammable" moments that IP owners desire.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ShoppingBag className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-lg font-semibold">Availability of Large Retail Spaces</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Online shopping has shifted consumer behavior, creating vacancies in high streets and malls. 
                  LBEs with anchor IP can activate these dormant commercial spaces.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Venue Requirements */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              Venue requirements
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our formatted approach makes site requirements clear and repeatable.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-['Geist'] text-xl mb-4">Space</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Approximately 1,600 sqm starting footprint</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Ceiling height: 3.5m minimum for immersive build</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Ground floor preferred for accessibility</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="font-['Geist'] text-xl mb-4">Infrastructure</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Power: adequate for AV, lighting, climate control</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Load-in access for modular set installation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>HVAC and acoustics for guest comfort</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="font-['Geist'] text-xl mb-4">Location</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Urban density: high-footfall commercial areas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Public transport access for broader catchment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Co-location with F&B, retail for halo effect</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="font-['Geist'] text-xl mb-4">Partnership</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Lease type: flexible terms for phased rollout</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Revenue share model available</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Co-marketing for mutual brand uplift</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl text-center">
          <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
            Have a space in mind?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-muted-foreground">
            Let's discuss how Nexus LBE can activate your property with a proven entertainment format.
          </p>
          <Link to="/contact">
            <Button size="lg" className="h-12 px-6 bg-[#FF6B9D] hover:bg-[#FF6B9D]/90 text-[#0F0B1F]">
              Get in Touch
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PropertyPartnersPage;
